import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi, customerProfileApi } from '../services/api';
import type { StaffProfile } from '../types';
import { showToast } from 'shared-ui';

const emptyContact = () => ({ name: '', phone: '', relationship: '' });

interface CustomerInteraction {
  id: string;
  kind: string;
  timestamp: string;
  title: string;
}

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [staffProfile, setStaffProfile] = useState<StaffProfile>({
    bio: '',
    emergencyContacts: [emptyContact()],
    instagram: '',
    facebook: '',
    linkedin: '',
    website: '',
    socialLinks: {},
    bankAccountName: '',
    bankAccountNumber: '',
    bankBSB: '',
    bankRoutingNumber: '',
    taxFileNumber: '',
    taxId: '',
    taxIdType: 'TFN',
    abn: '',
    category: '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [customerPreferences, setCustomerPreferences] = useState({
    emailConsent: true,
    smsConsent: false,
  });
  const [savingCustomerPreferences, setSavingCustomerPreferences] = useState(false);
  const [customerInteractions, setCustomerInteractions] = useState<CustomerInteraction[]>([]);

  useEffect(() => {
    setFirstName(user?.firstName ?? '');
    setLastName(user?.lastName ?? '');
    setPhone(user?.phone ?? '');
  }, [user?.firstName, user?.lastName, user?.phone]);

  useEffect(() => {
    const sp = user?.staffProfile;
    if (sp) {
      setStaffProfile({
        bio: sp.bio ?? '',
        emergencyContacts: sp.emergencyContacts?.length ? sp.emergencyContacts : [emptyContact()],
        instagram: sp.instagram ?? '',
        facebook: sp.facebook ?? '',
        linkedin: sp.linkedin ?? '',
        website: sp.website ?? '',
        socialLinks: sp.socialLinks ?? {},
        bankAccountName: sp.bankAccountName ?? '',
        bankAccountNumber: sp.bankAccountNumber ?? '',
        bankBSB: sp.bankBSB ?? '',
        bankRoutingNumber: sp.bankRoutingNumber ?? '',
        taxFileNumber: sp.taxFileNumber ?? '',
        taxId: sp.taxId ?? '',
        taxIdType: sp.taxIdType ?? 'TFN',
        abn: sp.abn ?? '',
        category: sp.category ?? '',
      });
    }
  }, [user?.staffProfile]);

  useEffect(() => {
    const loadCustomerData = async () => {
      try {
        const [preferencesResponse, interactionsResponse] = await Promise.all([
          customerProfileApi.getPreferences(),
          customerProfileApi.getInteractions(),
        ]);
        if (preferencesResponse?.preferences) {
          setCustomerPreferences({
            emailConsent: !!preferencesResponse.preferences.emailConsent,
            smsConsent: !!preferencesResponse.preferences.smsConsent,
          });
        }
        if (Array.isArray(interactionsResponse?.timeline)) {
          setCustomerInteractions(interactionsResponse.timeline);
        }
      } catch (error) {
        console.error('Failed to load customer interaction data:', error);
      }
    };
    loadCustomerData();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const cleanedProfile: StaffProfile = {
        ...staffProfile,
        emergencyContacts: staffProfile.emergencyContacts?.filter((c) => c.name || c.phone || c.relationship) ?? [],
      };
      const response = await authApi.updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim() || null,
        staffProfile: cleanedProfile as Record<string, unknown>,
      });
      if (response.success && response.user) {
        updateUser({
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          phone: response.user.phone ?? undefined,
          staffProfile: (response.user as { staffProfile?: StaffProfile }).staffProfile ?? undefined,
        });
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        showToast(response.error || 'Failed to save profile', 'error');
      }
    } catch (err) {
      console.error('Profile save failed:', err);
      showToast('Failed to save profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveCustomerPreferences = async () => {
    setSavingCustomerPreferences(true);
    try {
      await customerProfileApi.updatePreferences(customerPreferences);
    } catch (error) {
      console.error('Failed to save customer preferences:', error);
      showToast('Failed to save communication preferences', 'error');
    } finally {
      setSavingCustomerPreferences(false);
    }
  };

  const addEmergencyContact = () => {
    setStaffProfile((p) => ({
      ...p,
      emergencyContacts: [...(p.emergencyContacts || [emptyContact()]), emptyContact()],
    }));
  };
  const updateEmergencyContact = (i: number, field: string, val: string) => {
    setStaffProfile((p) => ({
      ...p,
      emergencyContacts: (p.emergencyContacts || [emptyContact()]).map((c, idx) =>
        idx === i ? { ...c, [field]: val } : c
      ),
    }));
  };
  const removeEmergencyContact = (i: number) => {
    setStaffProfile((p) => {
      const list = p.emergencyContacts || [emptyContact()];
      if (list.length <= 1) return p;
      return { ...p, emergencyContacts: list.filter((_, idx) => idx !== i) };
    });
  };

  const inputStyle = { width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px' };
  const labelStyle = { display: 'block', marginBottom: '6px', fontSize: '13px', color: '#666' };

  return (
    <div>
      <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>👤 My Profile</h1>
      <p style={{ margin: '10px 0 20px 0', color: '#666' }}>Manage your personal information</p>

      <Link
        to="/my-qr"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 16px',
          background: '#334155',
          color: 'white',
          borderRadius: '8px',
          textDecoration: 'none',
          fontSize: '14px',
          marginBottom: '20px',
        }}
      >
        🔲 My QR Code
      </Link>

      <div style={{ maxWidth: '600px' }}>
        {/* Avatar */}
        <div style={{
          background: 'white',
          padding: '30px',
          borderRadius: '12px',
          textAlign: 'center',
          marginBottom: '20px'
        }}>
          <div style={{
            width: '100px',
            height: '100px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)',
            margin: '0 auto 15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '40px',
            color: 'white'
          }}>
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </div>
          <h2 style={{ margin: '0 0 5px 0', color: '#333' }}>{user?.firstName} {user?.lastName}</h2>
          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>{user?.email}</p>
          <div style={{ marginTop: '10px' }}>
            <span style={{ fontSize: '12px', color: '#666', marginRight: '6px' }}>Mango ID:</span>
            <code style={{
              display: 'inline-block',
              padding: '5px 12px',
              background: '#f0f0f0',
              borderRadius: '6px',
              fontSize: '13px'
            }}>
              {user?.mangoId || '— Not linked. Link from your business Staff page to get a Mango ID'}
            </code>
          </div>
        </div>

        {/* Personal Info Form */}
        <div style={{ background: 'white', padding: '25px', borderRadius: '12px', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#333' }}>Personal Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
            <div>
              <label style={labelStyle}>First Name</label>
              <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Last Name</label>
              <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              value={user?.email ?? ''}
              readOnly
              style={{ ...inputStyle, background: '#f9f9f9' }}
            />
            <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#888' }}>Email cannot be changed here</p>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Phone</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Add phone number" style={inputStyle} />
          </div>
        </div>

        {/* Customer-Business Interaction Controls */}
        <div style={{ background: 'white', padding: '25px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #e8e8e8' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#333' }}>🤝 My Business Interactions</h3>
          <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#666' }}>
            Manage what communications you receive from businesses while preserving your transaction records.
          </p>

          <div style={{ marginBottom: '14px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', color: '#333' }}>
              <input
                type="checkbox"
                checked={customerPreferences.emailConsent}
                onChange={(e) => setCustomerPreferences((prev) => ({ ...prev, emailConsent: e.target.checked }))}
              />
              Promotional Email Consent
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#333' }}>
              <input
                type="checkbox"
                checked={customerPreferences.smsConsent}
                onChange={(e) => setCustomerPreferences((prev) => ({ ...prev, smsConsent: e.target.checked }))}
              />
              Promotional SMS Consent
            </label>
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#888' }}>
              Transactional notices (e.g. receipts/order updates) may still be sent when required.
            </p>
          </div>

          <button
            type="button"
            onClick={saveCustomerPreferences}
            disabled={savingCustomerPreferences}
            style={{
              padding: '10px 16px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: savingCustomerPreferences ? 'not-allowed' : 'pointer',
              marginBottom: '16px',
            }}
          >
            {savingCustomerPreferences ? 'Saving…' : 'Save Communication Preferences'}
          </button>

          <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>Recent Interaction Timeline</h4>
          {customerInteractions.length === 0 ? (
            <p style={{ margin: 0, color: '#999', fontStyle: 'italic', fontSize: '13px' }}>No interactions found yet.</p>
          ) : (
            <div style={{ maxHeight: '220px', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {customerInteractions.slice(0, 25).map((item) => (
                <div key={item.id} style={{ border: '1px solid #eee', borderRadius: '8px', padding: '10px', background: '#fafafa' }}>
                  <div style={{ fontWeight: 600, color: '#333', marginBottom: '3px' }}>{item.title}</div>
                  <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>{item.kind}</div>
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>{new Date(item.timestamp).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Shareable Staff Profile - shared with businesses when you accept their invitation */}
        <div style={{ background: 'white', padding: '25px', borderRadius: '12px', marginBottom: '20px', border: '1px solid #e8e8e8' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#333' }}>🥭 Shareable Staff Profile</h3>
          <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#666' }}>
            Fill this in once. When a business invites you as staff and you accept, they receive this info. You control what&apos;s shared—double handshake.
          </p>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Bio / Description</label>
            <textarea
              value={staffProfile.bio ?? ''}
              onChange={(e) => setStaffProfile((p) => ({ ...p, bio: e.target.value }))}
              placeholder="Brief bio or description..."
              rows={3}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Emergency Contacts</label>
            {(staffProfile.emergencyContacts || [emptyContact()]).map((c, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '8px', marginBottom: '8px', alignItems: 'end' }}>
                <input
                  placeholder="Name"
                  value={c.name}
                  onChange={(e) => updateEmergencyContact(i, 'name', e.target.value)}
                  style={inputStyle}
                />
                <input
                  placeholder="Phone"
                  value={c.phone}
                  onChange={(e) => updateEmergencyContact(i, 'phone', e.target.value)}
                  style={inputStyle}
                />
                <input
                  placeholder="Relationship"
                  value={c.relationship}
                  onChange={(e) => updateEmergencyContact(i, 'relationship', e.target.value)}
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={() => removeEmergencyContact(i)}
                  style={{ padding: '8px 12px', background: '#fee', color: '#c33', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
                >
                  Remove
                </button>
              </div>
            ))}
            <button type="button" onClick={addEmergencyContact} style={{ padding: '8px 16px', background: '#f0f0f0', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
              + Add Contact
            </button>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Social Media &amp; Online Presence</label>
            <div style={{ display: 'grid', gap: '10px' }}>
              <input placeholder="📸 Instagram (@username or URL)" value={staffProfile.instagram ?? ''} onChange={(e) => setStaffProfile((p) => ({ ...p, instagram: e.target.value }))} style={inputStyle} />
              <input placeholder="📘 Facebook (Profile URL)" value={staffProfile.facebook ?? ''} onChange={(e) => setStaffProfile((p) => ({ ...p, facebook: e.target.value }))} style={inputStyle} />
              <input placeholder="📎 LinkedIn (Profile URL)" value={staffProfile.linkedin ?? ''} onChange={(e) => setStaffProfile((p) => ({ ...p, linkedin: e.target.value }))} style={inputStyle} />
              <input placeholder="🌐 Website / Portfolio (https://...)" value={staffProfile.website ?? ''} onChange={(e) => setStaffProfile((p) => ({ ...p, website: e.target.value }))} style={inputStyle} />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Social handles for tagging</label>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Your @usernames so Mango friends can tag you when posting to X, Instagram, etc.</p>
            <div style={{ display: 'grid', gap: '10px' }}>
              <input placeholder="𝕏 X (Twitter) @handle" value={staffProfile.socialLinks?.x ?? ''} onChange={(e) => setStaffProfile((p) => ({ ...p, socialLinks: { ...p.socialLinks, x: e.target.value || undefined } }))} style={inputStyle} />
              <input placeholder="📸 Instagram @handle" value={staffProfile.socialLinks?.instagram ?? ''} onChange={(e) => setStaffProfile((p) => ({ ...p, socialLinks: { ...p.socialLinks, instagram: e.target.value || undefined } }))} style={inputStyle} />
              <input placeholder="📎 LinkedIn @handle or URL" value={staffProfile.socialLinks?.linkedin ?? ''} onChange={(e) => setStaffProfile((p) => ({ ...p, socialLinks: { ...p.socialLinks, linkedin: e.target.value || undefined } }))} style={inputStyle} />
              <input placeholder="▶ YouTube channel handle" value={staffProfile.socialLinks?.youtube ?? ''} onChange={(e) => setStaffProfile((p) => ({ ...p, socialLinks: { ...p.socialLinks, youtube: e.target.value || undefined } }))} style={inputStyle} />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Bank Details (for Payments)</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <input placeholder="Account Name" value={staffProfile.bankAccountName ?? ''} onChange={(e) => setStaffProfile((p) => ({ ...p, bankAccountName: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <input placeholder="Account Number" value={staffProfile.bankAccountNumber ?? ''} onChange={(e) => setStaffProfile((p) => ({ ...p, bankAccountNumber: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <input placeholder="BSB (Australia) e.g. 000-000" value={staffProfile.bankBSB ?? ''} onChange={(e) => setStaffProfile((p) => ({ ...p, bankBSB: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <input placeholder="Routing Number (US/Canada)" value={staffProfile.bankRoutingNumber ?? ''} onChange={(e) => setStaffProfile((p) => ({ ...p, bankRoutingNumber: e.target.value }))} style={inputStyle} />
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Tax Information</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ ...labelStyle, fontSize: '11px' }}>Type</label>
                <select
                  value={staffProfile.taxIdType ?? 'TFN'}
                  onChange={(e) => setStaffProfile((p) => ({ ...p, taxIdType: e.target.value }))}
                  style={inputStyle}
                >
                  <option value="TFN">TFN (Australia)</option>
                  <option value="SSN">SSN (US)</option>
                  <option value="SIN">SIN (Canada)</option>
                  <option value="EIN">EIN (US Business)</option>
                </select>
              </div>
              <div>
                <input placeholder="Tax File Number / SSN / SIN" value={staffProfile.taxId ?? staffProfile.taxFileNumber ?? ''} onChange={(e) => setStaffProfile((p) => ({ ...p, taxId: e.target.value, taxFileNumber: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <input placeholder="ABN (Australia, contractors)" value={staffProfile.abn ?? ''} onChange={(e) => setStaffProfile((p) => ({ ...p, abn: e.target.value }))} style={inputStyle} />
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            width: '100%',
            padding: '14px',
            background: saved ? '#28a745' : 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontWeight: '600',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.8 : 1,
          }}
        >
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default Profile;
